import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { UserRoles } from 'types/userRoles';
import { roleToText } from 'utils';

import { useUpdateUser, useUserRoles } from './queries';
import { schema } from './schema';
import { FormFields } from './types';
import { ControlledCheckboxField } from '../../../../components/FormControlsV2/ControlledCheckboxField';
import { ControlledDateTimeField } from '../../../../components/FormControlsV2/ControlledDateTimeField';
import { ControlledFileField } from '../../../../components/FormControlsV2/ControlledFileField';
import { ControlledMaskedTextField } from '../../../../components/FormControlsV2/ControlledMaskedTextField';
import { ControlledRadioGroupField } from '../../../../components/FormControlsV2/ControlledRadioGroupField';
import { ControlledSelectField } from '../../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextEditorField } from '../../../../components/FormControlsV2/ControlledTextEditorField';
import { ControlledTextField } from '../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../components/FormControlsV2/FormProvider';
import { ImagePreview } from '../../../../components/UIKit/ImagePreview';
import { Config$ } from '../../../../effector/config';
import { userInfo } from '../../../../effector/userInfoList';
import { AggregateUser, UserType } from '../../../../types/aggregateUser';

interface Props {
  user?: AggregateUser | null;
  onUpdate: () => void;
}

const userTypes: { value: string; label: string }[] = [
  { value: String(UserType.Insider), label: 'Сотрудник БКС' },
  { value: String(UserType.NonInsider), label: 'Не сотрудник БКС' },
  { value: String(UserType.Org), label: 'Организация' },
  { value: String(UserType.Representative), label: 'Представитель' },
];

export function UserForm({ user, onUpdate }: Props) {
  const [error, setError] = useState('');

  const configStore = useUnit(Config$);
  const userInfoStore = useUnit(userInfo);

  const { enqueueSnackbar } = useSnackbar();
  const { data: userRolesResult } = useUserRoles();
  const { mutateAsync, isPending: isLoading } = useUpdateUser();

  const isUpdate = !!user;

  const userRoles = useMemo(
    () =>
      userRolesResult?.result?.map((item, index) => ({
        id: index.toString(),
        name: roleToText(item),
        role: item as UserRoles,
      })) || [],
    [userRolesResult?.result],
  );

  const defaultValues = useMemo(() => {
    if (!user) {
      return {
        login: '',
        type: UserType.Insider,
        alias: '',
        rating: null,
        firstName: '',
        midName: '',
        lastName: '',
        position: '',
        orgName: '',
        org: '',
        inn: '',
        ogrn: '',
        kpp: '',
        passportId: '',
        passportIssued: '',
        issueDate: null as unknown as Date,
        birthDate: null as unknown as Date,
        placeOfBirth: '',
        employeeId: '',
        agreementNum: '',
        photo: '',
        hasComission: false,
        conflictOfInterest: false,
        phone: '',
        infoHtml: '',
        shortinfoHtml: '',
        infoText: '',
        shortinfoText: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: '',
      };
    }

    const role = userRoles.find((item) => item.role === user?.role);

    let phone = user.phone ? user.phone.trim() : '';

    if (phone.length === 10 && phone.match(/[0-9]{10}/)) {
      phone = '+7' + phone;
    }

    let org;
    if (user.masterId) {
      const orgId = userInfoStore.users.find((u) => u.id === user.masterId)?.id;
      org = orgId ? String(orgId) : undefined;
    }

    const data: Partial<FormFields> = {
      login: user.login,
      rating: user.rating ?? null,
      firstName: user.firstName,
      lastName: user.lastName,
      midName: user.midName,
      position: user.position,
      orgName: user.company,
      org: org,
      inn: user.orgInn,
      ogrn: user.orgOgrn,
      kpp: user.orgKpp,
      passportId: user.passportId,
      passportIssued: user.passportIssued,
      employeeId: user.employeeId,
      agreementNum: user.agreementNum ?? '',
      issueDate: user.issueDate ? new Date(user.issueDate) : null,
      birthDate: user.birthDate ? new Date(user.birthDate) : null,
      placeOfBirth: user.placeOfBirth ?? '',
      hasComission: !!user.comission,
      conflictOfInterest: !!user.conflictOfInterest,
      phone: phone,
      email: user.email ?? '',
      photo: user.photo,
      infoHtml: user.infoHtml,
      shortinfoHtml: user.shortinfoHtml,
      infoText: user.infoText,
      shortinfoText: user.shortinfoText,
      password: user.password,
      confirmPassword: user.password,
      role: role ? role.id : undefined,
      type: user.userType,
      alias: user.alias,
    };

    return data;
  }, [user, userRoles, userInfoStore.users]);

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormFields>({
    shouldUnregister: true,
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const getCreateData = useCallback(
    (values: FormFields) => {
      const role = userRoles.find(({ id }) => id === values.role);
      const phone = values.phone?.split(' ').join('');

      const data = {
        masterId: values.org ? Number(values.org) : null,
        login: values.login,
        userType: values.type,
        alias: values.alias,
        firstName: values.firstName,
        lastName: values.lastName,
        midName: values.midName,
        position: values.position,
        company: values.orgName,
        orgInn: values.inn && values.inn.length > 0 ? values.inn : undefined,
        orgOgrn: values.ogrn && values.ogrn.length > 0 ? values.ogrn : undefined,
        orgKpp: values.kpp && values.kpp.length > 0 ? values.kpp : undefined,
        passportId: values.passportId,
        issueDate: values.issueDate ? dayjs(values.issueDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        placeOfBirth: values.placeOfBirth,
        passportIssued: values.passportIssued,
        employeeId: values.employeeId,
        agreementNum: values.agreementNum,
        comission: values.hasComission,
        conflictOfInterest: values.conflictOfInterest,
        phone: phone,
        email: values.email,
        photo: values.photo ?? '',
        photoChanged: dirtyFields.photo,
        infoHtml: values.infoHtml ?? '',
        shortinfoHtml: values.shortinfoHtml,
        infoText: values.infoText,
        shortinfoText: values.shortinfoText,
        password: values.password && values.password.length > 0 ? values.password : undefined,
        role: role?.role,
        rating: values.rating != null ? Number(values.rating) : undefined,
      };

      return data;
    },
    [dirtyFields.photo, userRoles],
  );

  const getUpdateData = useCallback(
    (values: FormFields) => {
      const data = {
        ...user,
        ...getCreateData(values),
      };

      return data;
    },
    [getCreateData, user],
  );

  const onSubmit = async (data: FormFields) => {
    const requestData = isUpdate ? getUpdateData(data) : getCreateData(data);

    try {
      const result: { errorMessage?: string | null } = await mutateAsync(requestData);

      if (result.errorMessage) {
        setError(result.errorMessage);
        return;
      }

      onUpdate();
      enqueueSnackbar(isUpdate ? 'Данные пользователя изменены' : 'Пользователь добавлен');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setError(err?.message || 'Произошла непредвиденная ошибка');
    }
  };

  const organizations = useMemo(
    () => userInfoStore.users.filter((u) => u.userType === UserType.Org),
    [userInfoStore.users],
  );

  const organizationOptions = useMemo(
    () => organizations.map((u) => ({ value: String(u.id), label: u.orgName ?? '' })) || [],
    [organizations],
  );

  const userRoleOptions = useMemo(
    () => userRoles.map(({ id, name }) => ({ value: id, label: name })) || [],
    [userRoles],
  );

  const friendlyUrl = configStore?.friendlyUrl ?? '';

  const role = watch('role');
  const userType = watch('type');
  const photo = watch('photo');
  const org = watch('org');

  const isAuthor = role === '0';
  // Нужно поработать над типом userType т.к. по факту он String
  const isOrganization = isAuthor && userType == UserType.Org;
  const isRepresentative = isAuthor && userType == UserType.Representative;
  const isInsider = isAuthor && userType == UserType.Insider;
  const hasNoPassport = !isAuthor || userType != UserType.NonInsider;

  useEffect(() => {
    if (isAuthor && userType == UserType.Representative) {
      setValue('agreementNum', '');
    } else if (!isAuthor) {
      setValue('type', UserType.Insider);
      setValue('rating', null);
    }
  }, [isAuthor, userType, org, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
      <FormProvider control={control} errors={errors}>
        <Stack direction="row" gap={2}>
          <ControlledTextField name="login" label="Логин" disableAutocomplete />
          <ControlledTextField name="alias" label="Никнейм" disabled={!isAuthor} />
        </Stack>
        <ControlledSelectField name="role" options={userRoleOptions} label="Роль" />
        <ControlledRadioGroupField name="type" options={userTypes} label="Тип пользователя" row disabled={!isAuthor} />

        {isAuthor && (
          <ControlledTextField type="text" name="rating" label="Рейтинг" inputProps={{ inputMode: 'decimal' }} />
        )}

        {isRepresentative && (
          <ControlledSelectField name="org" label="Наименование организации" options={organizationOptions} />
        )}

        {isOrganization && <ControlledTextField name="orgName" label="Наименование организации" />}

        {isAuthor && userType == UserType.Org ? (
          <>
            <ControlledMaskedTextField name="inn" label="ИНН" mask="9999999999" />
            <ControlledMaskedTextField name="ogrn" label="ОГРН" mask="9999999999999" />
            <ControlledMaskedTextField name="kpp" label="КПП" mask="999999999" />
          </>
        ) : (
          <>
            <ControlledTextField name="lastName" label="Фамилия" />
            <ControlledTextField name="firstName" label="Имя" />
            <ControlledTextField name="midName" label="Отчество" />
          </>
        )}

        <Stack direction="row" gap={2}>
          <ControlledTextField name="position" label="Должность" disabled={isOrganization} />
          <ControlledTextField name="agreementNum" label="Номер договора" disabled={!isAuthor || isRepresentative} />
        </Stack>

        <Stack direction="row" gap={2}>
          <ControlledTextField name="employeeId" label="Табельный номер" disabled={!isInsider} />
          <ControlledMaskedTextField
            name="passportId"
            label="Серия номер паспорта"
            mask="99 99 999999"
            disabled={hasNoPassport}
          />
        </Stack>

        <Stack direction="row" gap={2}>
          <ControlledTextField name="passportIssued" label="Кем выдан" disabled={hasNoPassport} />
          <ControlledDateTimeField name="issueDate" label="Дата выдачи" format="DD.MM.YYYY" disabled={hasNoPassport} />
        </Stack>

        <Stack direction="row" gap={2}>
          <ControlledDateTimeField
            name="birthDate"
            label="Дата рождения"
            format="DD.MM.YYYY"
            disabled={hasNoPassport}
          />
          <ControlledTextField name="placeOfBirth" label="Место рождения" disabled={hasNoPassport} />
        </Stack>

        <Stack direction="row" gap={2}>
          <ControlledCheckboxField name="hasComission" label="Согласие разделить комиссию" disabled={!isAuthor} />
          <ControlledCheckboxField name="conflictOfInterest" label="Конфликт интересов" disabled={!isInsider} />
        </Stack>

        {isAuthor && (
          <>
            {photo && (
              <Box maxWidth={300}>
                <ImagePreview image={`${friendlyUrl}${photo}`} onDelete={() => setValue('photo', '')} />
              </Box>
            )}
            <ControlledFileField
              name="photo"
              placeholder="Изображение"
              url="api/admin/files/upload_author"
              fileTypes={['.png', '.jpg', '.gif']}
              sizeView="kb"
              onRemove={() => null}
              onUpload={(fieldName, fileName) => setValue('photo', fileName)}
            />
          </>
        )}

        <ControlledTextEditorField name="shortinfoHtml" label="Краткое описание" />
        <ControlledTextEditorField name="infoHtml" label="Полное описание" />
        <ControlledTextField name="shortinfoText" label="Краткое текстовое описание" multiline rows={4} />
        <ControlledTextField name="infoText" label="Текстовое описание" multiline rows={4} />

        <Stack direction="row" gap={2}>
          <ControlledMaskedTextField name="phone" label="Номер телефона" mask="+9 999 999 99 99" />
          <ControlledTextField type="email" name="email" label="Почтовый ящик" />
        </Stack>
        <Stack direction="row" gap={2}>
          <ControlledTextField type="password" name="password" label="Пароль" disableAutocomplete />
          <ControlledTextField type="password" name="confirmPassword" label="Повторить пароль" disableAutocomplete />
        </Stack>
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isDirty || isLoading}
        fullWidth
        sx={{ mt: 2 }}
        startIcon={isLoading && <CircularProgress size={20} />}
      >
        {isUpdate ? 'Обновить' : 'Добавить'}
      </Button>
    </form>
  );
}
